<template>
  <v-layout row justify-space-between>
    <v-flex grow>
      <EmployeeField
        v-if="employee.name"
        fieldType="text"
        label="Legacy Single Name Field"
        fieldName="name"
        :object="employee"
      />
      <EmployeeField
        fieldType="text"
        label="First Name"
        fieldName="first_name"
        :object="employee"
      />
      <EmployeeField
        fieldType="text"
        label="Last Name"
        fieldName="last_name"
        :object="employee"
      />
      <EmployeeField
        fieldType="text"
        label="Nick Name"
        fieldName="nick_name"
        :object="employee"
      />

      <EmployeeField
        fieldType="text"
        label="Accolades"
        fieldName="accolades"
        :object="employee"
      />

      <EmployeeCheckboxField
        fieldType="switch"
        label="Use Dr. Title"
        fieldName="dr_title"
        :object="employee"
      />

      <v-divider></v-divider>

      <EmployeeField
        fieldType="text"
        label="Street Address"
        fieldName="street_address"
        :object="employee"
      />
      <EmployeeField
        fieldType="text"
        label="City"
        fieldName="city"
        :object="employee"
      />

      <EmployeeSelect
        label="State"
        fieldName="state"
        :object="employee"
        :items="states"
      />

      <EmployeeField
        fieldType="text"
        label="Zip Code"
        fieldName="zip"
        :object="employee"
      />
      <EmployeeField
        fieldType="text"
        label="Phone"
        mask="phone"
        fieldName="phone"
        :object="employee"
        :hint="employee.sms_opt_out ? 'Opts out of SMS' : 'Receives SMS'"
        persistent-hint
      />
      <EmployeeField
        fieldType="text"
        label="Email"
        fieldName="email"
        :object="employee"
      />

      <v-flex v-if="user.managerOrHigher">
        <v-layout column>
          <v-flex class="mr-2">
            <v-text-field
              :disabled="!updatingPassword"
              label="Password"
              type="password"
              v-model="newPassword"
            >
            </v-text-field>
          </v-flex>
          <v-flex>
            <v-text-field
              :disabled="!updatingPassword"
              label="Password Confirmation"
              type="password"
              v-model="newPasswordConfirmation"
              :error-messages="
                newPassword && newPasswordConfirmation && !passwordsMatch
                  ? 'The password and confirmation do no match'
                  : ''
              "
            >
            </v-text-field>
          </v-flex>
        </v-layout>
        <v-btn
          v-if="!updatingPassword"
          color="warning"
          @click="updatingPassword = !updatingPassword"
          >Change Password</v-btn
        >
        <v-btn
          v-if="updatingPassword"
          color="warning"
          @click="saveNewPassword"
          :disabled="
            !(newPassword && newPasswordConfirmation) || !passwordsMatch
          "
          >Save and Update Password</v-btn
        >
        <v-btn
          color="grey"
          flat
          @click="cancelUpdatingPassword"
          v-if="updatingPassword"
          >Cancel</v-btn
        >
      </v-flex>

      <v-dialog v-model="welcomeEmailDialog" full-width max-width="300">
        <v-btn color="primary" block slot="activator">Send Welcome Email</v-btn>
        <v-card>
          <v-card-title>
            <h2 class="headline">Send Welcome Email?</h2>
          </v-card-title>
          <v-card-text>
            <p>
              This will send an initial welcome email to
              {{ employee.email }} with instructions on setting up their account
              and how to find onboarding resources.
            </p>
          </v-card-text>
          <v-spacer></v-spacer>
          <v-card-actions>
            <v-btn color="grey" flat @click="welcomeEmailDialog = false"
              >Nevermind</v-btn
            >
            <v-btn color="success" @click="sendWelcomeEmail"
              >Send the Email</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>

      <PasswordResetDialog :email="employee.email" />

      <v-dialog full-width v-model="passwordResetDialog" max-width="300">
        <v-btn color="primary" block slot="activator"
          >Send Password Reset Instructions</v-btn
        >
        <v-card>
          <v-card-title>
            <h2 class="headline">Reset Password?</h2>
          </v-card-title>
          <v-card-text>
            <p>
              This will send an email to {{ employee.email }} with instructions
              to reset their password. The password will not be changed until
              they proceed with the reset instructions.
            </p>
          </v-card-text>
          <v-spacer></v-spacer>
          <v-card-actions>
            <v-btn color="grey" flat @click="passwordResetDialog = false"
              >Nevermind</v-btn
            >
            <v-btn color="success" @click="sendPasswordReset"
              >Send the Email</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>

      <EmployeeDateField
        fieldType="date"
        label="Birthday"
        mask="date-with-time"
        fieldName="birthday"
        :object="employee"
      />
    </v-flex>

    <v-flex grow mx-4>
      <v-sheet color="grey lighten-3">
        <v-layout row pa-2>
          <v-flex mr-2 mb-2>
            <v-img
              v-if="employee.profile_picture_url"
              height="200"
              width="200"
              contain
              :src="employee.profile_picture_url"
            >
            </v-img>

            <a @click.prevent="showingUpload = !showingUpload" href="#"
              >Add/Edit Picture</a
            >

            <div v-show="showingUpload">
              <Uploader
                v-model="photo"
                accept="image/*"
                @presigned="profile_picture_data = $event"
              >
              </Uploader>

              <ImagePreview> </ImagePreview>

              <v-btn
                @click.prevent="savePhoto()"
                v-show="chosenFilename"
                :disabled="!presignedPresent"
                :loading="!presignedPresent"
                >Save</v-btn
              >
            </div>
          </v-flex>
          <v-flex>
            <h2>Bio</h2>
            <EmployeeField
              fieldType="textarea"
              label="Bio"
              fieldName="bio"
              :object="employee"
            />
          </v-flex>
        </v-layout>
      </v-sheet>

      <Referrer class="mt-2" :user="employee" />
    </v-flex>

    <v-flex shrink>
      <!-- TODO: move to <ErContactEditor employee="employee" /> -->

      <h2 class="all-caps">ER Contact</h2>
      <v-dialog v-model="removeERContactDialog" max-width="290">
        <v-card>
          <v-card-title class="headline">Remove ER Contact?</v-card-title>
          <v-card-text>
            This will remove {{ deleteDialogItem.name }}.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" flat @click="removeERContactDialog = false"
              >Nevermind</v-btn
            >
            <v-btn
              color="success"
              small
              :loading="apiCallPending"
              @click="removeERContact(deleteDialogItem)"
            >
              Add
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-layout
        @mouseover="showRemove = contact"
        @mouseout="showRemove = null"
        row
        align-center
        v-for="contact in employee.emergency_contacts"
        class="info-listing mb-2"
      >
        <v-flex
          shrink
          v-show="showRemove === contact"
          class="mr-1 transition-fast-in-fast-out"
        >
          <v-icon @click="openRemoveERContactDialog(contact)">cancel</v-icon>
        </v-flex>
        <v-flex>
          <ul class="info-listing mb-2">
            <li>{{ contact.name }} | {{ contact.relation }}</li>
            <li v-for="phone in contact.phone_numbers">
              <span>{{ phone.phone_type }}:</span> {{ phone.number | phone }}
            </li>
          </ul>
        </v-flex>
      </v-layout>

      <v-dialog v-model="newERContactDialog" max-width="350">
        <v-btn slot="activator">
          <v-icon left>
            add
          </v-icon>
          Add New
        </v-btn>
        <v-card>
          <v-card-title class="headline">Add ER Contact</v-card-title>
          <v-card-text>
            <v-text-field label="Contact Name" v-model="contactName">
            </v-text-field>
            <v-text-field label="Relationship" v-model="contactRelation">
            </v-text-field>
            <v-select
              :items="phoneTypes"
              label="Phone Type"
              v-model="contactPhone1Type"
            >
            </v-select>
            <v-text-field
              label="Number"
              mask="phone"
              v-model="contactPhone1Number"
            >
            </v-text-field>
            <v-select
              :items="phoneTypes"
              label="Phone Type"
              v-model="contactPhone2Type"
            >
            </v-select>
            <v-text-field
              label="Number"
              mask="phone"
              v-model="contactPhone2Number"
            >
            </v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" flat @click="newERContactDialog = false"
              >Nevermind</v-btn
            >
            <v-btn
              color="success"
              small
              :loading="apiCallPending"
              @click="createERContact"
            >
              Add
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-divider></v-divider>

      <v-btn small disabled>
        Reset Password
      </v-btn>
      <v-btn small disabled>
        Reset PIN
      </v-btn>
    </v-flex>
  </v-layout>
</template>

<script>
import { mapGetters, mapState } from "vuex";
// import Info from '@/components/hr/info.vue'
import EmployeeField from "@/components/hr/employee-field.vue";
import EmployeeDateField from "@/components/hr/employee-date-field.vue";
import EmployeeCheckboxField from "@/components/hr/employee-checkbox-field.vue";
import EmployeeSelect from "@/components/hr/employee-select.vue";
import PasswordResetDialog from "@/components/hr/password-reset-dialog.vue";
import Uploader from "@/components/hr/uploader.vue";
import ImagePreview from "@/components/hr/image-preview.vue";
import Referrer from "@/components/hr/referrer.vue";
import emailsApi from "@/api/emails";
import collectionsApi from "@/api/collections";

export default {
  components: {
    // Info,
    EmployeeField,
    EmployeeDateField,
    EmployeeCheckboxField,
    EmployeeSelect,
    PasswordResetDialog,
    Uploader,
    ImagePreview,
    Referrer
  },
  props: ["employee", "user"],
  computed: {
    presignedPresent: function() {
      if (this.profile_picture_data && this.profile_picture_data.id) {
        return true;
      } else {
        return false;
      }
    },
    passwordsMatch() {
      if (this.newPassword == this.newPasswordConfirmation) {
        return true;
      }
      return false;
    },
    chosenFilename: function() {
      return this.$store.state.uploader.fileName;
    }
  },
  methods: {
    saveNewPassword() {
      this.$store
        .dispatch("employees/update", {
          employee: {
            id: this.employee.id,
            password: this.newPassword,
            password_confirmation: this.newPasswordConfirmation
          }
        })
        .then(res => {
          this.$store.dispatch("global/showSnackbarSuccess", {
            message: "Password set! Be sure to let the employee know."
          });
        })
        .catch(error => {
          console.log(error);
          this.$store.dispatch("global/showSnackbarError", {
            message: "Something went wrong setting that password..."
          });
        })
        .finally(res => {
          this.cancelUpdatingPassword();
        });
    },
    cancelUpdatingPassword() {
      this.newPassword = null;
      this.newPasswordConfirmation = null;
      this.updatingPassword = false;
    },
    savePhoto() {
      this.$store
        .dispatch("employees/savePhoto", {
          id: this.employee.id,
          profile_picture_data: this.profile_picture_data
        })
        .finally(res => {
          this.$store.commit("uploader/setImage", null);
          this.$store.commit("uploader/setFilename", "");
          this.showingUpload = false;
        });
    },
    sendPasswordReset() {
      this.$store
        .dispatch("userAuth/requestPasswordReset", this.employee.email)
        .then(() => {
          this.passwordResetDialog = false;
          this.$store.dispatch("global/showSnackbarSuccess", {
            message: "A password reset email has been sent!"
          });
        })
        .catch(err => {
          this.passwordResetDialog = false;
          this.$store.dispatch("global/showSnackbarError", {
            message: "Something went wrong sending that password reset."
          });
        });
    },
    sendWelcomeEmail() {
      emailsApi.sendWelcomeEmail(
        this.employee.id,
        res => {
          this.welcomeEmailDialog = false;
          this.$store.dispatch("global/showSnackbarSuccess", {
            message: "A welcome email has been sent!"
          });
        },
        error => {
          this.welcomeEmailDialog = false;
          this.$store.dispatch("global/showSnackbarError", {
            message: "Something went wrong sending that welcome email."
          });
        }
      );
    },
    openRemoveERContactDialog(contact) {
      this.removeERContactDialog = true;
      this.deleteDialogItem = contact;
    },
    removeERContact(contact) {
      this.apiCallPending = true;
      this.$store
        .dispatch("employees/removeERContact", contact.id)
        .finally(res => {
          this.apiCallPending = false;
          this.removeERContactDialog = false;
        });
    },
    createERContact() {
      this.apiCallPending = true;
      var params = {
        id: this.employee.id,
        emergency_contacts_attributes: [
          {
            name: this.contactName,
            relation: this.contactRelation,
            phone_numbers_attributes: []
          }
        ]
      };
      if (this.contactPhone1Type && this.contactPhone1Number) {
        params.emergency_contacts_attributes[0].phone_numbers_attributes.push({
          phone_type: this.contactPhone1Type,
          number: this.contactPhone1Number
        });
      }
      if (this.contactPhone2Type && this.contactPhone2Number) {
        params.emergency_contacts_attributes[0].phone_numbers_attributes.push({
          phone_type: this.contactPhone2Type,
          number: this.contactPhone2Number
        });
      }

      this.$store.dispatch("employees/update", params).finally(res => {
        this.apiCallPending = false;
        this.newERContactDialog = false;
        this.contactName = "";
        this.contactRelation = "";
        this.contactPhone1Type = "";
        this.contactPhone1Number = "";
        this.contactPhone2Type = "";
        this.contactPhone2Number = "";
      });
    }
  },
  data: function() {
    return {
      photo: null,
      profile_picture_data: null,
      showingUpload: false,
      showRemove: null,
      newERContactDialog: false,
      removeERContactDialog: false,
      deleteDialogItem: {},
      apiCallPending: false,
      contactName: "",
      contactRelation: "",
      contactPhone1Type: "Cell",
      contactPhone1Number: "",
      contactPhone2Type: "",
      contactPhone2Number: "",
      phoneTypes: ["Home", "Work", "Cell", "Other"],
      passwordResetDialog: false,
      welcomeEmailDialog: false,
      updatingPassword: false,
      newPassword: null,
      newPasswordConfirmation: null,
      states: collectionsApi.getStates()
    };
  }
};
</script>
